import { getAuthoredFitnessDemonstration } from "@/lib/fitnessAuthoredDemonstrationCatalog";
import type { FitnessEquipmentKind } from "@/lib/fitnessExerciseEquipment";
import { canonicalFitnessExerciseName } from "@/lib/fitnessExerciseIdentity";
/** Exact, provenance-bound motions. Technical admission is separate from professional exercise review. */
export interface ExerciseAvatarDemonstration {
  id: string;
  assetUrl: string;
  modelUrl: string;
  modelOffsetY?: number;
  clipName: string;
  equipment?: { kind: FitnessEquipmentKind; metadataUrl: string };
  quality: 'technical-preview';
  sourceUrl: string;
  bounds: { min: readonly [number, number, number]; max: readonly [number, number, number] };
}

const SOURCE = 'https://github.com/Mesh2Motion/mesh2motion-app/blob/653a9698f5f315523072f1c3ff3496100401317b/static/animations/human-addon-animations.glb';
const common = {
  assetUrl: '/fitness/vyv-fitness-mp-pilot.glb',
  modelUrl: '/avatar/model_small.glb',
  quality: 'technical-preview' as const,
  sourceUrl: SOURCE,
};
const pushUp: ExerciseAvatarDemonstration = {
  ...common, id: 'push-up', clipName: 'Pushup',
  bounds: { min: [-0.480418, 0.073178, -1.001748], max: [0.479501, 0.758736, 0.852551] },
};
const jumpingJack: ExerciseAvatarDemonstration = {
  ...common, id: 'jumping-jack', clipName: 'Jumping Jacks',
  bounds: { min: [-0.869637, -0.018799, -0.204568], max: [0.866375, 2.154463, 0.196205] },
};

const squat: ExerciseAvatarDemonstration = {
  id: 'squat', modelOffsetY: 0, assetUrl: '/fitness/authored/squat.glb', modelUrl: '/avatar/model_small.glb',
  clipName: 'VYV authored bodyweight squat prototype', quality: 'technical-preview',
  sourceUrl: 'https://github.com/82rmartins/vyv-mirror-ai/blob/codex/vyv-fitness-rea-20261015/src/lib/fitnessAuthoredMotion.ts',
  bounds: { min: [-0.317351, 0, -0.342949], max: [0.317363, 1.815753, 0.651284] },
};

const plank: ExerciseAvatarDemonstration = {
  id: 'plank', modelOffsetY: 0, assetUrl: '/fitness/authored/plank.glb', modelUrl: '/avatar/model_small.glb',
  clipName: 'VYV authored plank candidate', quality: 'technical-preview',
  sourceUrl: 'https://github.com/82rmartins/vyv-mirror-ai/blob/codex/vyv-fitness-rea-20261015/src/lib/fitnessAuthoredCore.ts',
  bounds: {"min": [-0.2394335888275805, 0.0002613687240057977, -0.9607953405628734], "max": [0.23963446239674424, 0.3998898993639269, 0.9337762211459683]},
};

const crunches: ExerciseAvatarDemonstration = {
  id: 'crunches', modelOffsetY: 0, assetUrl: '/fitness/authored/crunch.glb', modelUrl: '/avatar/model_small.glb',
  clipName: 'VYV authored crunch candidate', quality: 'technical-preview',
  sourceUrl: 'https://github.com/82rmartins/vyv-mirror-ai/blob/codex/vyv-fitness-rea-20261015/src/lib/fitnessAuthoredCore.ts',
  bounds: {"min": [-0.23759600054146746, 1.141131407908476e-15, -0.8772805262359135], "max": [0.24157941539102987, 0.46162043282184884, 0.8543342992774197]},
};



function normalizeExercise(value: string): string {
  return value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}
const pushUpAliases = new Set(['push up', 'push ups', 'pushup', 'pushups', 'flexao', 'flexoes', 'flexao de braco', 'flexao de bracos', 'flexoes de bracos']);
const squatAliases = new Set(['squat', 'squats', 'bodyweight squat', 'body weight squat', 'agachamento', 'agachamento livre']);
const jumpingJackAliases = new Set(['jumping jack', 'jumping jacks', 'jumpingjack', 'jumpingjacks', 'polichinelo', 'polichinelos']);

/** Exact aliases only: an incline/decline/one-arm push-up must never get the standard push-up clip. */
export function getExerciseAvatarDemonstration(exerciseIdOrName: string): ExerciseAvatarDemonstration | null {
  const key = normalizeExercise(canonicalFitnessExerciseName(exerciseIdOrName));
  if (pushUpAliases.has(key)) return pushUp;
  if (jumpingJackAliases.has(key)) return jumpingJack;
  if (squatAliases.has(key)) return squat;
  if (['plank', 'forearm plank', 'prancha', 'prancha nos antebracos'].includes(key)) return plank;
  if (['crunch', 'crunches', 'abdominal curto'].includes(key)) return crunches;
  return getAuthoredFitnessDemonstration(canonicalFitnessExerciseName(exerciseIdOrName));
}

export function fitnessDemonstrationPlaybackRate(value: number): number {
  return Number.isFinite(value) ? Math.max(0.25, Math.min(2, value)) : 1;
}
